/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, ICoreContext, IImageSettings, Image } from '@msdyn365-commerce/core';
import { CartLine, format, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl, getProductPageUrlSync, ObjectExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, IncrementalQuantity } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

/**
 * Data required to render each quick order row.
 */
export interface IQuickOrderTableRowData {
    cartLine: CartLine;
    product: SimpleProduct;
}

/**
 * Resources for quick order table rows.
 */
export interface IQuickOrderTableRowResources {
    quickOrderQuantityMobileText: string;
    quickOrderProductDimensionsSeparator: string;
    quickOrderViewDetailsButtonText: string;
    quickOrderViewDetailsButtonAriaLabel: string;
    quickOrderRemoveItemButtonAriaLabel: string;
    quickOrderInputQuantityAriaLabel: string;
    quickOrderDecrementButtonAriaLabel: string;
    quickOrderIncrementButtonAriaLabel: string;
}

/**
 * Interface for quick order table props.
 */
export interface IQuickOrderTableRowProps extends IComponentProps<IQuickOrderTableRowData> {
    context: ICoreContext;
    className: string;

    imageSettings?: IImageSettings;
    isMobile: boolean;
    resources: IQuickOrderTableRowResources;
}

/**
 * The default image settings for quick order table row.
 */
const defaultImageSettings: IImageSettings = {
    viewports: {
        xs: { q: 'w=80&h=80&m=6', w: 0, h: 0 },
        lg: { q: 'w=147&h=147&m=6', w: 0, h: 0 }
    },
    lazyload: true,
    cropFocalRegion: true
};

/**
 * Renders product image for quick order row.
 * @param props - Quick order table row props.
 * @returns React element.
 */
const QuickOrderTableRowProductImageComponent: React.FC<IQuickOrderTableRowProps> = (props: IQuickOrderTableRowProps) => {
    const fallbackImageUrl = getFallbackImageUrl(
        props.data.product.ItemId,
        props.context.actionContext.requestContext.apiSettings,
        props.context.request.app?.config?.OmniChannelMedia
    );

    const containerClassName = `${props.className}__product-image-container`;

    return (
        <div className={containerClassName}>
            <Image
                requestContext={props.context.actionContext.requestContext}
                className={`${containerClassName}__product-image`}
                altText={props.data.product.Name}
                src={props.data.product.PrimaryImageUrl ?? ''}
                fallBackSrc={fallbackImageUrl}
                gridSettings={props.context.request.gridSettings!}
                imageSettings={props.imageSettings ?? defaultImageSettings}
                loadFailureBehavior='empty'
            />
        </div>
    );
};

/**
 * Renders actions popup for the mobile view.
 * @param props - Configuration for popup.
 * @returns A react node with actions..
 */
const QuickOrderExtraActionsPopupComponent: React.FC<IQuickOrderTableRowProps> = (props: IQuickOrderTableRowProps): JSX.Element | null => {
    const product: SimpleProduct = props.data.product;

    let productUrl = '';
    if (!StringExtensions.isNullOrWhitespace(product.Name) && !ObjectExtensions.isNullOrUndefined(product.RecordId)) {
        productUrl = getProductPageUrlSync(product.Name!, product.RecordId, props.context.actionContext);
    }

    if (StringExtensions.isNullOrWhitespace(productUrl)) {
        return null;
    }

    const className = `${props.className}__extra-actions-cell`;

    return (
        <div className={className}>
            <Button className={`${className}__toggle`} />
        </div>
    );
};

/**
 * Renders quick order table rows for desktop view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of quick order rows for desktop view.
 */
export const QuickOrderDesktopTableRowCellComponent: React.FC<IQuickOrderTableRowProps> = (props: IQuickOrderTableRowProps) => {
    const product: SimpleProduct = props.data.product;
    const cartLine: CartLine = props.data.cartLine;
    const columnWithNumberClassName = `${props.className}__column-with-number`;

    let productUrl = '';
    if (!StringExtensions.isNullOrWhitespace(product.Name) && !ObjectExtensions.isNullOrUndefined(product.RecordId)) {
        productUrl = getProductPageUrlSync(product.Name!, product.RecordId, props.context.actionContext);
    }

    return (
        <>
            <td>
                <QuickOrderTableRowProductImageComponent {...props} />
            </td>
            <td>{product.ItemId}</td>
            <td>
                <div className={`${props.className}__product-info`}>
                    <a className={`${props.className}__product-name`} href={productUrl}>
                        {product.Name}
                    </a>
                    <span className={`${props.className}__product-dimensions`}>
                        {product.Dimensions?.map(dimension => dimension.DimensionValue?.Value)
                            .filter(dimensionValue => !StringExtensions.isNullOrWhitespace(dimensionValue))
                            .join(props.resources.quickOrderProductDimensionsSeparator)}
                    </span>
                </div>
            </td>
            <td className={columnWithNumberClassName}>{cartLine.Price}</td>
            <td>{cartLine.UnitOfMeasureSymbol}</td>
            <td className={`${props.className}__product-quantity`}>
                <IncrementalQuantity
                    id={`msc-quick-order__quantity_${product.RecordId}-${cartLine.LineId ?? ''}`}
                    max={10}
                    currentCount={cartLine.Quantity}
                    inputQuantityAriaLabel={props.resources.quickOrderInputQuantityAriaLabel}
                    decrementButtonAriaLabel={props.resources.quickOrderDecrementButtonAriaLabel}
                    incrementButtonAriaLabel={props.resources.quickOrderIncrementButtonAriaLabel}
                    key={cartLine.LineId}
                    isGiftCard={product.IsGiftCard}
                />
            </td>
            <td className={columnWithNumberClassName}>{cartLine.TotalAmount}</td>
            <td>
                <Button className={`${props.className}__product-remove`} aria-label={props.resources.quickOrderRemoveItemButtonAriaLabel} />
            </td>
        </>
    );
};

/**
 * Renders rows for mobile view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of quick order rows for mobile view.
 */
export const QuickOrderMobileTableRowCellComponent: React.FC<IQuickOrderTableRowProps> = (props: IQuickOrderTableRowProps) => {
    const product: SimpleProduct = props.data.product;
    const cartLine: CartLine = props.data.cartLine;

    return (
        <>
            <td>
                <QuickOrderTableRowProductImageComponent {...props} />
            </td>
            <td>
                <div className={`${props.className}__product-info`}>
                    <span className={`${props.className}__product-name`}>{product.ItemId}</span>
                    <span className={`${props.className}__product-quantity`}>
                        {format(props.resources.quickOrderQuantityMobileText, cartLine.Quantity)}
                    </span>
                </div>
            </td>
            <td>
                <QuickOrderExtraActionsPopupComponent {...props} />
            </td>
        </>
    );
};

/**
 * Quick order table row component.
 * @param props - Configuration of the component.
 * @returns React node for the row.
 */
export const QuickOrderTableRowComponent: React.FC<IQuickOrderTableRowProps> = (props: IQuickOrderTableRowProps) => {
    const rowClassName = `${props.className}__row`;

    let headerCells: JSX.Element;
    if (props.isMobile) {
        headerCells = <QuickOrderMobileTableRowCellComponent {...props} />;
    } else {
        headerCells = <QuickOrderDesktopTableRowCellComponent {...props} />;
    }

    return (
        <thead className={props.className}>
            <tr className={rowClassName}>{headerCells}</tr>
        </thead>
    );
};
